/**
 * CRUD THUNKS FACTORY — Pattern ripetuto per ogni entita' del core:
 *
 *   fetchAll(params?)   -> Page<T>      (list, paginata, requireGroupId)
 *   fetchOne(id)        -> T            (single, no groupId)
 *   create(payload)     -> T            (requireGroupId)
 *   update(payload)     -> T            (no groupId, PATCH /singolare/{id})
 *   remove(id)          -> number       (id del record cancellato)
 *
 * Il factory genera i 5 thunk standard per entita' che seguono la
 * convenzione `*Api` = `{ getAll(groupId, params), getById(id),
 * create(groupId, payload), update(payload), remove(id) }`.
 *
 * Action types generati: `${name}/fetchAll`, `${name}/fetchOne`,
 * `${name}/create`, `${name}/update`, `${name}/delete`. Coerenti con
 * il naming usato prima del refactor, cosi' i devtools Redux non
 * cambiano nome dopo la migrazione.
 *
 * Il chiamante esegue il destructure con alias per produrre gli export
 * tipici (`fetchTasks`, `createTask`, ...):
 *
 * ```ts
 * const { fetchAll, fetchOne, create, update, remove } =
 *   createCrudThunks<Task, TaskCreatePayload, TaskUpdatePayload, ServerListParams>(
 *     'tasks', taskApi,
 *   );
 * export { fetchAll as fetchTasks, fetchOne as fetchTask,
 *          create as createTask, update as updateTask, remove as deleteTask };
 * ```
 *
 * Thunks custom (es. `analyzeMessage`) continuano a vivere nel file
 * `*Thunks.ts` accanto a quelli standard.
 */

import { createAsyncThunk, type AsyncThunk } from '@reduxjs/toolkit';
import type { Page } from '@crm/shared';
import type { RootState } from './store';
import { requireGroupId, extractError } from './thunkHelpers';

/** Contratto api accettato dal factory: rispecchia il pattern dei `*Api.ts`. */
export interface CrudApi<T, CreateP, UpdateP, ListP> {
  getAll: (groupId: number, params?: ListP) => Promise<Page<T>>;
  getById: (id: number) => Promise<T>;
  create: (groupId: number, payload: CreateP) => Promise<T>;
  update: (payload: UpdateP) => Promise<T>;
  remove: (id: number) => Promise<void>;
}

export interface CrudThunks<T, CreateP, UpdateP, ListP> {
  fetchAll: AsyncThunk<Page<T>, ListP | undefined, { state: RootState; rejectValue: string }>;
  fetchOne: AsyncThunk<T, number, { rejectValue: string }>;
  create: AsyncThunk<T, CreateP, { state: RootState; rejectValue: string }>;
  update: AsyncThunk<T, UpdateP, { rejectValue: string }>;
  remove: AsyncThunk<number, number, { rejectValue: string }>;
}

export function createCrudThunks<T, CreateP, UpdateP, ListP>(
  name: string,
  api: CrudApi<T, CreateP, UpdateP, ListP>,
): CrudThunks<T, CreateP, UpdateP, ListP> {
  return {
    fetchAll: createAsyncThunk<Page<T>, ListP | undefined, { state: RootState; rejectValue: string }>(
      `${name}/fetchAll`,
      async (params, { getState, rejectWithValue }) => {
        try { return await api.getAll(requireGroupId(getState), params); }
        catch (err: unknown) { return rejectWithValue(extractError(err)); }
      },
    ),
    fetchOne: createAsyncThunk<T, number, { rejectValue: string }>(
      `${name}/fetchOne`,
      async (id, { rejectWithValue }) => {
        try { return await api.getById(id); }
        catch (err: unknown) { return rejectWithValue(extractError(err)); }
      },
    ),
    create: createAsyncThunk<T, CreateP, { state: RootState; rejectValue: string }>(
      `${name}/create`,
      async (payload, { getState, rejectWithValue }) => {
        try { return await api.create(requireGroupId(getState), payload); }
        catch (err: unknown) { return rejectWithValue(extractError(err)); }
      },
    ),
    update: createAsyncThunk<T, UpdateP, { rejectValue: string }>(
      `${name}/update`,
      async (payload, { rejectWithValue }) => {
        try { return await api.update(payload); }
        catch (err: unknown) { return rejectWithValue(extractError(err)); }
      },
    ),
    remove: createAsyncThunk<number, number, { rejectValue: string }>(
      `${name}/delete`,
      async (id, { rejectWithValue }) => {
        try { await api.remove(id); return id; }
        catch (err: unknown) { return rejectWithValue(extractError(err)); }
      },
    ),
  };
}
